/**
 * Print an image by URL using a hidden iframe.
 *
 * Creates a temporary iframe, loads the image, triggers print dialog,
 * then removes the iframe automatically.
 *
 * @param imageUrl - The URL of the image to print
 */
export function printImage(imageUrl: string): void {
  const iframe = document.createElement("iframe")
  iframe.style.position = "fixed"
  iframe.style.right = "0"
  iframe.style.bottom = "0"
  iframe.style.width = "0"
  iframe.style.height = "0"
  iframe.style.border = "none"
  document.body.appendChild(iframe)

  const iframeDoc = iframe.contentWindow?.document
  if (!iframeDoc) {
    document.body.removeChild(iframe)
    return
  }

  const cleanup = () => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe)
    }
  }

  if (!iframe.contentWindow) {
    cleanup()
    return
  }

  // Set up callbacks on iframe's window - each iframe has isolated scope
  const iframeWindow = iframe.contentWindow as Window & {
    onImageLoad?: () => void
    onImageError?: () => void
  }

  iframeWindow.onImageLoad = () => {
    // Clean up when print dialog closes
    iframeWindow.addEventListener("afterprint", cleanup)

    iframe.contentWindow?.focus()
    iframe.contentWindow?.print()
  }

  iframeWindow.onImageError = cleanup

  iframeDoc.open()
  iframeDoc.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          @media print {
            body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
            img { max-width: 100%; max-height: 100vh; object-fit: contain; }
          }
        </style>
      </head>
      <body>
        <img src="${imageUrl}" onload="onImageLoad()" onerror="onImageError()" />
      </body>
    </html>
  `)
  iframeDoc.close()
}
